import { Fragment, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon, PencilIcon, PlusIcon } from '@heroicons/react/outline'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { fetchScheduleRoster, saveScheduleRoster, fetchWarehouseOperators } from '../../../services/workforceServices'
import { extractBackendError, formatIsoDate, invalidateWorkforce, toRosterEntry } from '../../../utils/workforceUtils'
import { Loader } from '../../../components/Loader'

const ALWAYS_ON_END = '2099-12-31'

const useDebounced = (value, delay) => {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return v
}

export const PeriodRosterSlidePanel = ({ warehouse_id, period, onEdit, onClose }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)
  const [overridesByOp, setOverridesByOp] = useState({}) // user_warehouse_id -> { included: bool, isDefault: bool, full_name, operator_type, contract_type }
  const [picker, setPicker] = useState(false)
  const [pickerQuery, setPickerQuery] = useState('')
  const [baseError, setBaseError] = useState('')
  const debouncedQuery = useDebounced(pickerQuery, 300)

  useEffect(() => {
    setOpen(true)
  }, [])
  const close = (didChange = false) => {
    setOpen(false)
    setTimeout(() => onClose(didChange), 250)
  }

  const { data: rosterData, isLoading } = useQuery(
    ['workforce', warehouse_id, 'schedule-roster', period.id],
    () => fetchScheduleRoster(warehouse_id, period.id),
    { refetchOnWindowFocus: false },
  )

  useEffect(() => {
    if (!rosterData) return
    const operators = rosterData.operators || rosterData.roster || (Array.isArray(rosterData) ? rosterData : [])
    const next = {}
    for (const op of operators) {
      const entry = toRosterEntry(op, period.work_shift?.id)
      next[entry.user_warehouse_id] = entry
    }
    setOverridesByOp(next)
  }, [rosterData, period.work_shift?.id])

  const { data: searchData, isFetching: isSearching } = useQuery(
    ['workforce', warehouse_id, 'operators', { for_picker: true, q: debouncedQuery }],
    () => fetchWarehouseOperators(warehouse_id, { full_name_or_email: debouncedQuery, include_inactive: false }),
    { enabled: picker && debouncedQuery.length >= 2, keepPreviousData: true },
  )
  const searchResults = useMemo(() => {
    const raw = Array.isArray(searchData) ? searchData : searchData?.warehouse_operators || searchData?.operators || []
    return raw.filter(op => !overridesByOp[op.id])
  }, [searchData, overridesByOp])

  const saveMutation = useMutation(
    () => {
      // Only send deviations from default behavior:
      //  - default operators that the supervisor excluded
      //  - non-default operators that the supervisor added (always included: true)
      const overrides = Object.values(overridesByOp)
        .filter(o => (o.isDefault && !o.included) || (!o.isDefault && o.included))
        .map(o => ({ user_warehouse_id: o.user_warehouse_id, included: o.included }))
      return saveScheduleRoster(warehouse_id, period.id, { overrides })
    },
    {
      onSuccess: () => {
        invalidateWorkforce(queryClient, warehouse_id, ['schedules', 'schedule-roster', 'attendance'])
        close(true)
      },
      onError: err => {
        const { message } = extractBackendError(err)
        setBaseError(message)
      },
    },
  )

  const togglePresence = uwId => {
    setOverridesByOp(prev => ({
      ...prev,
      [uwId]: { ...prev[uwId], included: !prev[uwId].included },
    }))
  }

  const addOperator = op => {
    setOverridesByOp(prev => ({
      ...prev,
      [op.id]: {
        user_warehouse_id: op.id,
        full_name: op.user_name || op.user?.full_name || op.full_name,
        operator_type: op.operator_type,
        contract_type: op.contract_type,
        isDefault: false,
        included: true,
      },
    }))
    setPickerQuery('')
    setPicker(false)
  }

  const removeAdded = uwId => {
    setOverridesByOp(prev => {
      const next = { ...prev }
      delete next[uwId]
      return next
    })
  }

  const orderedRows = useMemo(() => {
    const all = Object.values(overridesByOp)
    const defaults = all.filter(o => o.isDefault).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
    const added = all.filter(o => !o.isDefault).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
    return { defaults, added }
  }, [overridesByOp])

  const periodLabel = period.end_date === ALWAYS_ON_END ? `${period.start_date} → ∞` : `${period.start_date} → ${period.end_date}`
  const shift = period.work_shift || {}

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-20 overflow-hidden" open={open} onClose={() => close(false)}>
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-700 bg-opacity-40" />
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-lg">
                <div className="h-full flex flex-col bg-white shadow-xl">
                  <div className="px-6 py-4 border-b flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Dialog.Title className="text-lg font-medium truncate">{shift.name || t('workforce.schedule.period_roster')}</Dialog.Title>
                      <div className="text-sm text-gray-500 truncate">{periodLabel}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={onEdit}
                        title={t('workforce.schedule.edit_period')}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => close(false)} className="rounded-md p-1.5 text-gray-400 hover:text-gray-500">
                        <XIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {baseError && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{baseError}</div>}

                    <p className="text-xs text-gray-500">{t('workforce.schedule.roster_hint')}</p>

                    {isLoading ? (
                      <Loader show={true} />
                    ) : (
                      <>
                        <Section
                          title={t('workforce.schedule.default_operators')}
                          badgeClass="bg-gray-50 text-gray-600 border-gray-200"
                          count={orderedRows.defaults.length}
                          empty={t('workforce.schedule.no_defaults')}
                        >
                          {orderedRows.defaults.map(r => (
                            <RosterRow
                              key={r.user_warehouse_id}
                              row={r}
                              mode="default"
                              onToggle={() => togglePresence(r.user_warehouse_id)}
                              tLabel={r.included ? t('workforce.schedule.included') : t('workforce.schedule.excluded')}
                              untilLabel={
                                r.assignmentEndDate && r.assignmentEndDate <= period.end_date
                                  ? t('workforce.schedule.roster_until', { date: formatIsoDate(r.assignmentEndDate) })
                                  : null
                              }
                            />
                          ))}
                        </Section>

                        <Section
                          title={t('workforce.schedule.added_operators')}
                          badgeClass="bg-indigo-50 text-indigo-700 border-indigo-200"
                          count={orderedRows.added.length}
                          empty={t('workforce.schedule.no_added')}
                          action={
                            <button
                              type="button"
                              onClick={() => setPicker(true)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <PlusIcon className="h-3.5 w-3.5" />
                              {t('workforce.schedule.add_operator')}
                            </button>
                          }
                        >
                          {picker && (
                            <div className="border rounded-md p-2 mb-2 bg-gray-50">
                              <input
                                type="text"
                                autoFocus
                                value={pickerQuery}
                                onChange={e => setPickerQuery(e.target.value)}
                                placeholder={t('workforce.attendance.search_operator')}
                                className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                              />
                              {debouncedQuery.length >= 2 && (
                                <div className="mt-2 max-h-48 overflow-y-auto border rounded-md divide-y bg-white">
                                  {isSearching && <div className="p-2 text-xs text-gray-500">{t('workforce.operators.searching')}</div>}
                                  {!isSearching && searchResults.length === 0 && (
                                    <div className="p-2 text-xs text-gray-500">{t('workforce.operators.no_results')}</div>
                                  )}
                                  {!isSearching &&
                                    searchResults.map(op => (
                                      <button
                                        key={op.id}
                                        type="button"
                                        onClick={() => addOperator(op)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                      >
                                        <div className="font-medium">{op.user_name || op.user?.full_name || op.full_name}</div>
                                        <div className="text-xs text-gray-500">
                                          {op.operator_type} · {op.contract_type}
                                        </div>
                                      </button>
                                    ))}
                                </div>
                              )}
                              <div className="mt-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPicker(false)
                                    setPickerQuery('')
                                  }}
                                  className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                  {t('workforce.cancel')}
                                </button>
                              </div>
                            </div>
                          )}
                          {orderedRows.added.map(r => (
                            <RosterRow
                              key={r.user_warehouse_id}
                              row={r}
                              mode="added"
                              onRemove={() => removeAdded(r.user_warehouse_id)}
                              tLabel={t('workforce.schedule.included')}
                            />
                          ))}
                        </Section>
                      </>
                    )}
                  </div>

                  <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => close(false)}
                      disabled={saveMutation.isLoading}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {t('workforce.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => saveMutation.mutate()}
                      disabled={saveMutation.isLoading || isLoading}
                      className="px-4 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {saveMutation.isLoading ? t('workforce.saving') : t('workforce.save')}
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

const Section = ({ title, count, badgeClass, action, empty, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border ${badgeClass}`}>{count}</span>
      </div>
      {action}
    </div>
    <div className="divide-y border rounded-md bg-white">{count === 0 ? <div className="p-3 text-xs text-gray-400 text-center">{empty}</div> : children}</div>
  </div>
)

const RosterRow = ({ row, mode, onToggle, onRemove, tLabel, untilLabel }) => {
  const muted = !row.included
  return (
    <div className={`px-3 py-2 flex items-center justify-between gap-3 ${muted ? 'bg-red-50/40' : ''}`}>
      <div className="min-w-0">
        <div className={`text-sm font-medium truncate ${muted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{row.full_name || '—'}</div>
        <div className="text-xs text-gray-500 truncate">
          {row.operator_type} · {row.contract_type}
          {untilLabel && <> · {untilLabel}</>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${row.included ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
        >
          {tLabel}
        </span>
        {mode === 'default' ? (
          <button type="button" onClick={onToggle} className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
            {row.included ? '−' : '+'}
          </button>
        ) : (
          <button type="button" onClick={onRemove} className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
            ×
          </button>
        )}
      </div>
    </div>
  )
}
